import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileExport,
  faPlus,
  faFileExcel,
  faFilePdf,
  faPlane,
  faEye,
  faPencilAlt,
  faTrash,
  faTrashRestore
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store/hooks';
import { deleteAircraft, permanentDeleteAircraft, restoreAircraft } from '../../../store/slices/aircraftSlice';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import toast from 'react-hot-toast';
import api from '../../../services/api';

/**
 * Admin Aircraft Management Component
 * This component is used by system administrators to manage aircraft for a specific airliner
 * For airliner-specific aircraft management, see: /src/airliner/components/AircraftManagement/AircraftManagement.js
 */
const AircraftManagement = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: airliner_id } = useParams();
  const [aircraft, setAircraft] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [paginationInfo, setPaginationInfo] = useState({
    totalRecords: 0,
    totalPages: 1,
    currentPage: 1,
    perPage: 10
  });
  const [visibleColumns, setVisibleColumns] = useState({
    model: true,
    regNo: true,
    seats: true,
    endurance: true,
    status: true,
    action: true
  });
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'index', direction: 'desc' });
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [aircraftToDelete, setAircraftToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('soft');

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load aircraft for specific airliner on mount and when filters change
  useEffect(() => {
    if (!airliner_id) {
      navigate('/admin/airliners');
      return;
    }

    const loadAircraft = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Map frontend sort keys to backend field names
        const sortFieldMap = {
          'index': 'id',
          'model': 'id', // Can't sort by related model on backend easily
          'regNo': 'registration_number',
          'seats': 'seating_capacity',
          'endurance': 'id', // No endurance sorting on backend
          'status': 'id' // No status sorting on backend
        };
        
        const backendSortField = sortConfig.key ? sortFieldMap[sortConfig.key] || 'id' : 'id';
        const backendSortOrder = sortConfig.direction === 'asc' ? 'ASC' : 'DESC';
        
        const response = await api.get(`/admin/airliners/${airliner_id}/aircrafts`, {
          params: {
            page: currentPage,
            limit: recordsPerPage,
            search: debouncedSearchTerm || undefined,
            sortBy: backendSortField,
            sortOrder: backendSortOrder
          }
        });
        
        setAircraft(response.data || []);
        
        // Update pagination info from API response
        if (response.pagination) {
          setPaginationInfo({
            totalRecords: response.pagination.totalRecords || 0,
            totalPages: response.pagination.totalPages || 1,
            currentPage: response.pagination.currentPage || 1,
            perPage: response.pagination.perPage || recordsPerPage
          });
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch aircraft');
        console.error('Error fetching aircraft:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAircraft();
  }, [airliner_id, currentPage, recordsPerPage, debouncedSearchTerm, sortConfig, navigate]);

  const displayData = aircraft || [];
  const totalRecords = paginationInfo.totalRecords;
  const totalPages = paginationInfo.totalPages;
  const startIndex = (paginationInfo.currentPage - 1) * paginationInfo.perPage;
  const endIndex = Math.min(startIndex + displayData.length, totalRecords);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const handleExport = (format) => {
    const visibleCols = Object.keys(visibleColumns).filter(key => visibleColumns[key]);

    const header = visibleCols.map(key => {
      const labels = {
        model: 'Aircraft Model',
        regNo: 'Reg No.',
        seats: 'Seat(s)',
        endurance: 'Endurance',
        status: 'Status',
        action: 'Action'
      };
      return labels[key];
    }).join(',');

    const rows = displayData.map(aircraftItem => {
      return visibleCols.map(key => {
        switch (key) {
          case 'model':
            return aircraftItem.aircraftmodel
              ? `${aircraftItem.aircraftmodel.manufacturer}/${aircraftItem.aircraftmodel.model}/${aircraftItem.aircraftmodel.type_designator}`
              : aircraftItem.aircraft_modual || '';
          case 'regNo':
            return (aircraftItem.registration_number || '').toUpperCase();
          case 'seats':
            return aircraftItem.seating_capacity || '';
          case 'endurance':
            return aircraftItem.endurance ? new Date(`1970-01-01T${aircraftItem.endurance}`).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '';
          case 'status':
            return aircraftItem.is_draft === 1 ? 'Draft' : 'Added';
          default:
            return '';
        }
      }).join(',');
    });

    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `aircraft_data.${format === 'excel' ? 'csv' : 'pdf'}`;
    link.click();
    window.URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const formatEndurance = (endurance) => {
    if (!endurance) return '';
    try {
      const date = new Date(`1970-01-01T${endurance}`);
      return date.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return endurance;
    }
  };

  const handleDeleteAircraft = (aircraftId, isPermanent = false) => {
    setAircraftToDelete(aircraftId);
    setDeleteType(isPermanent ? 'permanent' : 'soft');
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (deleteType === 'permanent') {
        await dispatch(permanentDeleteAircraft(aircraftToDelete)).unwrap();
        toast.success('Aircraft permanently deleted successfully!');
      } else {
        await dispatch(deleteAircraft(aircraftToDelete)).unwrap();
        toast.success('Aircraft deleted successfully!');
      }
      // Refresh the aircraft list
      const response = await api.get(`/admin/airliners/${airliner_id}/aircrafts`, {
        params: {
          page: currentPage,
          limit: recordsPerPage,
          search: debouncedSearchTerm || undefined
        }
      });
      setAircraft(response.data || []);
      
      // Update pagination info
      if (response.pagination) {
        setPaginationInfo({
          totalRecords: response.pagination.totalRecords || 0,
          totalPages: response.pagination.totalPages || 1,
          currentPage: response.pagination.currentPage || 1,
          perPage: response.pagination.perPage || recordsPerPage
        });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete aircraft');
    } finally {
      setShowDeleteConfirmation(false);
      setAircraftToDelete(null);
      setDeleteType('soft');
    }
  };

  const handleAddFlight = (aircraftItem) => {
    if (aircraftItem.is_draft === 1) {
      toast.error('This Aircraft is Saved but NOT added. Please edit, enter or review all information and proceed to "Add Aircraft" before adding a Flight.');
      return;
    }
    navigate(`/admin/aircraft/route/${aircraftItem.id}`, {
      state: { from: `/admin/airliners/${airliner_id}/aircrafts` }
    });
  };

  const handleViewAircraft = (aircraftItem) => {
    navigate(`/admin/airliners/${airliner_id}/aircrafts/${aircraftItem.id}`);
  };

  const handleEditAircraft = (aircraftItem) => {
    navigate(`/admin/airliners/${airliner_id}/aircrafts/${aircraftItem.id}/edit`);
  };

  const handleRestoreAircraft = async (aircraftId) => {
    try {
      await dispatch(restoreAircraft(aircraftId)).unwrap();
      toast.success('Aircraft restored successfully!');
      // Refresh the aircraft list
      const response = await api.get(`/admin/airliners/${airliner_id}/aircrafts`, {
        params: {
          page: currentPage,
          limit: recordsPerPage,
          search: debouncedSearchTerm || undefined
        }
      });
      setAircraft(response.data || []);
      
      // Update pagination info
      if (response.pagination) {
        setPaginationInfo({
          totalRecords: response.pagination.totalRecords || 0,
          totalPages: response.pagination.totalPages || 1,
          currentPage: response.pagination.currentPage || 1,
          perPage: response.pagination.perPage || recordsPerPage
        });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to restore aircraft');
    }
  };

  return (
    <div className="p-4 w-full h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 max-md:flex-col max-md:items-start max-md:gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          {/* <FontAwesomeIcon icon={faPlane} className="text-blue-600" /> */}
          <span className="font-semibold text-gray-800">Aircraft Management</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center w-full md:w-auto">
          {/* Export Dropdown */}
          <div className="relative w-full sm:w-auto">
            <button
              className="text-black h-9 w-full sm:w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
              onClick={() => setShowExportDropdown(!showExportDropdown)}
            >
              <FontAwesomeIcon icon={faFileExport} /> Export
            </button>
            {showExportDropdown && (
              <div className="absolute top-full left-0 bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border border-transparent rounded p-0 shadow-[0_2px_8px_rgba(0,0,0,0.15)] z-10 min-w-[155px] mt-[5px] overflow-hidden">
                <div onClick={() => handleExport('excel')} className="py-3 px-4 cursor-pointer text-[14px] text-black font-normal transition-colors duration-200 flex items-center gap-2.5 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]">
                  <FontAwesomeIcon icon={faFileExcel} /> Excel
                </div>
                <div onClick={() => handleExport('pdf')} className="py-3 px-4 cursor-pointer text-[14px] text-black font-normal transition-colors duration-200 flex items-center gap-2.5 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]">
                  <FontAwesomeIcon icon={faFilePdf} /> PDF
                </div>
              </div>
            )}
          </div>

          {/* Column Visibility Dropdown */}
          <div className="relative w-full sm:w-auto">
            <button
              className="text-black h-9 w-full sm:w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
              onClick={() => setShowColumnDropdown(!showColumnDropdown)}
            >
              Hide/Show Column ▼
            </button>
            {showColumnDropdown && (
              <div className="absolute top-full right-0 bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border border-transparent rounded py-2.5 px-0 shadow-[0_2px_8px_rgba(0,0,0,0.15)] z-10 min-w-[250px] mt-[5px] max-h-[400px] overflow-y-auto">
                {Object.keys(visibleColumns).map(key => (
                  <label
                    key={key}
                    onClick={() => toggleColumnVisibility(key)}
                    className="flex items-center py-2 px-5 cursor-pointer text-[14px] text-black font-normal transition-colors duration-200 gap-2.5 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
                  >
                    <span className="inline-flex items-center justify-center">
                      <span className="inline-flex items-center justify-center w-4 h-4 min-w-[16px] border border-[#666] rounded-sm bg-white">
                        {visibleColumns[key] && <span className="text-[#2ecc71] text-[14px] font-bold leading-none">✓</span>}
                      </span>
                    </span>
                    <span className="flex-1 whitespace-nowrap">
                      {key === 'model' && 'Aircraft Model'}
                      {key === 'regNo' && 'Reg No.'}
                      {key === 'seats' && 'Seat(s)'}
                      {key === 'endurance' && 'Endurance'}
                      {key === 'status' && 'Status'}
                      {key === 'action' && 'Action'}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Add Aircraft Button */}
          <button
            onClick={() => navigate(`/admin/airliners/${airliner_id}/aircrafts/create`)}
            className="text-black h-9 w-full sm:w-auto py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
          >
            <FontAwesomeIcon icon={faPlus} /> Add Aircraft
          </button>

          {/* Back Button */}
          <button
            onClick={() => navigate('/admin/airliners')}
            className="text-black h-9 w-full sm:w-auto py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
          >
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded shadow-sm border border-gray-100 flex-1 flex flex-col overflow-hidden">
        {/* Search Bar and Records Per Page */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700 whitespace-nowrap">Display</label>
            <select
              value={recordsPerPage}
              onChange={handleRecordsPerPageChange}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <label className="text-sm text-gray-700 whitespace-nowrap">Records</label>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex-1 max-w-xs">
              <div className="relative">
                <label className="text-sm text-gray-700 mr-2">Search</label>
                <input
                  type="text"
                  placeholder=""
                  value={searchTerm}
                  onChange={handleSearch}
                  className="px-4 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            {/* Pagination Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1 || totalPages === 0}
                className="px-4 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors bg-white"
              >
                Previous
              </button>

              <div className="flex gap-1">
                {totalPages > 0 && Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`min-w-[36px] px-3 py-1.5 border rounded text-sm transition-colors ${currentPage === pageNum
                        ? 'bg-[#ffd200] text-black border-[#ffd200] font-semibold'
                        : 'border-gray-300 hover:bg-gray-50 bg-white'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-4 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors bg-white"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">Loading aircraft...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-red-500">Error: {error}</div>
            </div>
          ) : displayData.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">No aircraft found</div>
            </div>
          ) : (
            <table className="w-full border-collapse bg-white min-w-[600px]">
              <thead className="bg-white border-2 border-[#e0e0e0]">
                <tr>
                  <th onClick={() => handleSort('index')} className="py-1.5 px-2.5 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-white border-t-0 border border-[#f4f4f4] align-middle whitespace-nowrap pr-[30px] cursor-pointer hover:bg-[#f0f0f0] transition-colors">
                    <span className="flex items-center gap-1 justify-between w-100"># <span className="text-[#999]">⇅</span></span>
                  </th>
                  {visibleColumns.model && <th onClick={() => handleSort('model')} className="py-1.5 px-2.5 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-white border-t-0 border border-[#f4f4f4] align-middle whitespace-nowrap cursor-pointer relative pr-[30px] hover:bg-[#f0f0f0] transition-colors">
                    <span className="flex items-center gap-1 justify-between w-100">Aircraft Model <span className="text-[#999]">⇅</span></span>
                  </th>}
                  {visibleColumns.regNo && <th onClick={() => handleSort('regNo')} className="py-1.5 px-2.5 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-white border-t-0 border border-[#f4f4f4] align-middle whitespace-nowrap cursor-pointer relative pr-[30px] hover:bg-[#f0f0f0] transition-colors">
                    <span className="flex items-center gap-1 justify-between w-100">Reg No. <span className="text-[#999]">⇅</span></span>
                  </th>}
                  {visibleColumns.seats && <th onClick={() => handleSort('seats')} className="py-1.5 px-2.5 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-white border-t-0 border border-[#f4f4f4] align-middle whitespace-nowrap cursor-pointer relative pr-[30px] hover:bg-[#f0f0f0] transition-colors">
                    <span className="flex items-center gap-1 justify-between w-100">Seat(s) <span className="text-[#999]">⇅</span></span>
                  </th>}
                  {visibleColumns.endurance && <th onClick={() => handleSort('endurance')} className="py-1.5 px-2.5 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-white border-t-0 border border-[#f4f4f4] align-middle whitespace-nowrap cursor-pointer relative pr-[30px] hover:bg-[#f0f0f0] transition-colors">
                    <span className="flex items-center gap-1 justify-between w-100">Endurance <span className="text-[#999]">⇅</span></span>
                  </th>}
                  {visibleColumns.status && <th onClick={() => handleSort('status')} className="py-1.5 px-2.5 text-left font-semibold text-black border-b-0 text-[12px] border-r border-[#e8e8e8] bg-white border-t-0 border border-[#f4f4f4] align-middle whitespace-nowrap cursor-pointer relative pr-[30px] hover:bg-[#f0f0f0] transition-colors">
                    <span className="flex items-center gap-1 justify-between w-100">Status <span className="text-[#999]">⇅</span></span>
                  </th>}
                  {visibleColumns.action && <th className="py-1.5 px-2.5 text-left font-semibold text-black border-2 text-[12px] border-r border-[#e8e8e8] bg-white border-t-0 border border-[#f4f4f4] align-middle whitespace-nowrap cursor-pointer relative pr-[30px] hover:bg-[#f0f0f0] transition-colors">
                    Action
                  </th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 1} className="text-center p-5 text-[#aaa] text-[13px] font-light">
                      Loading...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 1} className="text-center p-5 text-red-500 text-[13px] font-light">
                      Error: {error}
                    </td>
                  </tr>
                ) : displayData.length === 0 ? (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 1} className="text-center p-5 text-[#aaa] text-[13px] font-light">
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  displayData.map((aircraftItem, index) => (
                    <tr key={aircraftItem.id} className="transition-colors duration-200 hover:bg-[#f9f9f9]">
                      <td className="py-3.5 px-4 border-b text-center border-[#f0f0f0] text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white border border-[#f4f4f4] border-t-0 text-[12px] align-middle whitespace-nowrap">{((paginationInfo.currentPage - 1) * paginationInfo.perPage) + index + 1}</td>
                      {visibleColumns.model && <td className="py-3.5 px-4 text-center border-b border-[#f0f0f0] text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white border border-[#f4f4f4] border-t-0 text-[12px] align-middle whitespace-nowrap">
                        {aircraftItem.aircraftmodel
                          ? `${aircraftItem.aircraftmodel.model}/${aircraftItem.aircraftmodel.type_designator}`
                          : aircraftItem.aircraft_modual || ''
                        }
                      </td>}
                      {visibleColumns.regNo && <td className="py-3.5 px-4 border-b text-center border-[#f0f0f0] text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white border border-[#f4f4f4] border-t-0 text-[12px] align-middle whitespace-nowrap">
                        {(aircraftItem.registration_number || '').toUpperCase()}
                      </td>}
                      {visibleColumns.seats && <td className="py-3.5 px-4 border-b text-center border-[#f0f0f0] text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white border border-[#f4f4f4] border-t-0 text-[12px] align-middle whitespace-nowrap">
                        {aircraftItem.seating_capacity}
                      </td>}
                      {visibleColumns.endurance && <td className="py-3.5 px-4 text-center border-b border-[#f0f0f0] text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white border border-[#f4f4f4] border-t-0 text-[12px] align-middle whitespace-nowrap">
                        {formatEndurance(aircraftItem.endurance)}
                      </td>}
                      {visibleColumns.status && <td className="py-3.5 px-4 text-center border-b border-[#f0f0f0] text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white border border-[#f4f4f4] border-t-0 text-[12px] align-middle whitespace-nowrap">
                        {aircraftItem.is_draft === 1 ? (
                          <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-orange-500 rounded">Draft</span>
                        ) : (
                          <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-green-500 rounded">Added</span>
                        )}
                      </td>}
                      {visibleColumns.action && <td className="py-3.5 px-4 text-end border-b border-[#f0f0f0] text-[13px] text-[#666] border-r border-[#f5f5f5] bg-white border border-[#f4f4f4] border-t-0 text-[12px] align-middle whitespace-nowrap">
                        {aircraftItem.deleted_at ? (
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => handleRestoreAircraft(aircraftItem.id)}
                              className="w-8 h-8 flex items-center justify-center bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                              title="Restore Aircraft"
                            >
                              <FontAwesomeIcon icon={faTrashRestore} className="text-xs" />
                            </button>
                            <button
                              onClick={() => handleDeleteAircraft(aircraftItem.id, true)}
                              className="w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                              title="Permanently Delete"
                            >
                              <FontAwesomeIcon icon={faTrash} className="text-xs" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => handleAddFlight(aircraftItem)}
                              className={`w-8 h-8 flex items-center justify-center text-white rounded transition-colors ${aircraftItem.is_draft === 1
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-500 hover:bg-blue-600'
                                }`}
                              title={aircraftItem.is_draft === 1 
                                ? 'This Aircraft is Saved but NOT added. Please edit, enter or review all information and proceed to "Add Aircraft" before adding a Flight.'
                                : 'Add Flight'
                              }
                              disabled={aircraftItem.is_draft === 1}
                            >
                              <FontAwesomeIcon icon={faPlane} className="text-xs" />
                            </button>
                            <button
                              onClick={() => handleViewAircraft(aircraftItem)}
                              className="w-8 h-8 flex items-center justify-center bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                              title="Show Aircraft Details"
                            >
                              <FontAwesomeIcon icon={faEye} className="text-xs" />
                            </button>
                            {(!aircraftItem.flight_count || aircraftItem.flight_count === 0) && (
                              <>
                                <button
                                  onClick={() => handleEditAircraft(aircraftItem)}
                                  className="w-8 h-8 flex items-center justify-center bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                                  title="Edit Aircraft Details"
                                >
                                  <FontAwesomeIcon icon={faPencilAlt} className="text-xs" />
                                </button>
                                <button
                                  onClick={() => handleDeleteAircraft(aircraftItem.id, false)}
                                  className="w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                                  title="Delete Aircraft"
                                >
                                  <FontAwesomeIcon icon={faTrash} className="text-xs" />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Showing entries info at bottom */}
        {displayData.length > 0 && (
          <div className="p-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1} to {endIndex} of {totalRecords} entries
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <UniversalConfirmModal
        isOpen={showDeleteConfirmation}
        title={deleteType === 'permanent' ? 'Permanently Delete Aircraft' : 'Delete Aircraft'}
        message={
          deleteType === 'permanent'
            ? 'Are you sure you want to permanently delete this aircraft? This action cannot be undone.'
            : 'Are you sure you want to delete this aircraft? You can restore it later.'
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowDeleteConfirmation(false);
          setAircraftToDelete(null);
          setDeleteType('soft');
        }}
        isDangerous={deleteType === 'permanent'}
      />
    </div>
  );
};

export default AircraftManagement;
